import { createEntityClient, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type {
  IssueWorkflowOutDto,
  StageCreateInDto,
  StageReorderInDto,
  StageUpdateInDto,
  WorkflowCreateInDto,
  WorkflowDecisionInDto,
  WorkflowDecisionOutDto,
  WorkflowStageOutDto,
  WorkflowUpdateInDto,
} from './types';
import { workflowContract } from './workflowContract';

/**
 * Issue workflows of the selected project, as served by the service worker.
 * Changes resolve with what the server saved and reject with its error
 * (`ChannelRequestError.messageId`).
 */
export const workflowClient = {
  ...createEntityClient(workflowContract),

  createWorkflow(workflowCreateDto: WorkflowCreateInDto, projectId: string): Promise<IssueWorkflowOutDto> {
    return runQueuedMutation(
      workflowContract,
      'createWorkflow',
      (uniqueId) => ({ workflowCreateDto, projectId, uniqueId }),
      DomainMessagesTypes.createdWorkflow,
      { failureType: DomainMessagesTypes.workflowError }
    );
  },

  editWorkflow(
    workflowId: string,
    workflowUpdateDto: WorkflowUpdateInDto,
    projectId: string
  ): Promise<IssueWorkflowOutDto> {
    return runQueuedMutation(
      workflowContract,
      'editWorkflow',
      (uniqueId) => ({ workflowId, workflowUpdateDto, projectId, uniqueId }),
      DomainMessagesTypes.updatedWorkflow,
      { failureType: DomainMessagesTypes.workflowError }
    );
  },

  createStage(workflowId: string, stageCreateDto: StageCreateInDto, projectId: string): Promise<WorkflowStageOutDto> {
    return runQueuedMutation(
      workflowContract,
      'createStage',
      (uniqueId) => ({ workflowId, stageCreateDto, projectId, uniqueId }),
      DomainMessagesTypes.createdStage,
      { failureType: DomainMessagesTypes.stageError }
    );
  },

  editStage(
    workflowId: string,
    stageId: string,
    stageUpdateDto: StageUpdateInDto,
    projectId: string
  ): Promise<WorkflowStageOutDto> {
    return runQueuedMutation(
      workflowContract,
      'editStage',
      (uniqueId) => ({ workflowId, stageId, stageUpdateDto, projectId, uniqueId }),
      DomainMessagesTypes.updatedStage,
      { failureType: DomainMessagesTypes.stageError }
    );
  },

  deleteStage(workflowId: string, stageId: string, projectId: string): Promise<WorkflowStageOutDto> {
    return runQueuedMutation(
      workflowContract,
      'deleteStage',
      (uniqueId) => ({ workflowId, stageId, projectId, uniqueId }),
      DomainMessagesTypes.deletedStage,
      { failureType: DomainMessagesTypes.stageError }
    );
  },

  reorderStage(
    workflowId: string,
    stageId: string,
    stageReorderDto: StageReorderInDto,
    projectId: string
  ): Promise<WorkflowStageOutDto> {
    return runQueuedMutation(
      workflowContract,
      'reorderStage',
      (uniqueId) => ({ workflowId, stageId, stageReorderDto, projectId, uniqueId }),
      DomainMessagesTypes.reorderedStage,
      { failureType: DomainMessagesTypes.stageError }
    );
  },

  createDecision(
    projectId: string,
    issueId: string,
    decisionDto: WorkflowDecisionInDto
  ): Promise<WorkflowDecisionOutDto> {
    return runQueuedMutation(
      workflowContract,
      'createDecision',
      (uniqueId) => ({ projectId, issueId, decisionDto, uniqueId }),
      DomainMessagesTypes.createdDecision,
      { failureType: DomainMessagesTypes.decisionError }
    );
  },
};
